import React, { useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ArrowsInSimple } from "@phosphor-icons/react";
import { station, fmt } from "../lib/api";

export const StakeoutPanel = ({ disabled }) => {
  const [tx, setTx] = useState("");
  const [ty, setTy] = useState("");
  const [tz, setTz] = useState("");
  const [delta, setDelta] = useState(null);
  const [busy, setBusy] = useState(false);

  const compute = async () => {
    if (disabled) return;
    if (!tx || !ty || !tz) return;
    setBusy(true);
    try {
      const d = await station.stakeout({
        x: parseFloat(tx),
        y: parseFloat(ty),
        z: parseFloat(tz),
      });
      setDelta(d);
    } finally {
      setBusy(false);
    }
  };

  const arrived = delta?.arrived;
  const magnitude = (n) => Math.min(1, Math.abs(n) / 5); // saturate at 5m

  return (
    <div className="panel" data-testid="stakeout-panel">
      <div className="panel-header">
        <span>STAKEOUT</span>
        {arrived && (
          <span className="text-[10px] tracking-widest text-[var(--neon)] glow-green">ON TARGET</span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 p-4">
        <Coord testid="stakeout-x-input" label="X" value={tx} onChange={setTx} />
        <Coord testid="stakeout-y-input" label="Y" value={ty} onChange={setTy} />
        <Coord testid="stakeout-z-input" label="Z" value={tz} onChange={setTz} />
      </div>

      <div className="grid grid-cols-3 gap-2 px-4">
        <Delta
          testid="stakeout-forward"
          label={delta?.forward >= 0 ? "FORWARD" : "BACK"}
          icon={delta?.forward >= 0 ? ArrowUp : ArrowDown}
          value={delta ? Math.abs(delta.forward) : null}
          intensity={delta ? magnitude(delta.forward) : 0}
          arrived={arrived}
        />
        <Delta
          testid="stakeout-lateral"
          label={delta?.right >= 0 ? "RIGHT" : "LEFT"}
          icon={delta?.right >= 0 ? ArrowRight : ArrowLeft}
          value={delta ? Math.abs(delta.right) : null}
          intensity={delta ? magnitude(delta.right) : 0}
          arrived={arrived}
        />
        <Delta
          testid="stakeout-vertical"
          label={delta?.up >= 0 ? "UP" : "DOWN"}
          icon={delta?.up >= 0 ? ArrowUp : ArrowDown}
          value={delta ? Math.abs(delta.up) : null}
          intensity={delta ? magnitude(delta.up) : 0}
          arrived={arrived}
        />
      </div>

      <div className="flex items-center justify-between border-t border-[var(--border)] px-4 py-3">
        <div className="text-xs text-[var(--text-dim)] readout tabular-nums">
          2D: {fmt(delta?.distance_2d, 3)} m · 3D: {fmt(delta?.distance_3d, 3)} m
        </div>
        <button
          data-testid="stakeout-compute-btn"
          onClick={compute}
          disabled={busy || disabled || !tx || !ty || !tz}
          className="btn-tac !min-h-[44px] px-4 text-sm"
          data-variant="primary"
        >
          <span className="flex items-center gap-1">
            <ArrowsInSimple size={16} weight="bold" /> COMPUTE
          </span>
        </button>
      </div>
    </div>
  );
};

const Coord = ({ testid, label, value, onChange }) => (
  <label className="block">
    <span className="mb-1 block text-[10px] uppercase tracking-widest text-[var(--text-dim)]">{label}</span>
    <input
      data-testid={testid}
      type="number"
      step="0.001"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="0.000"
      className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-base tabular-nums text-[var(--text)] focus:border-[var(--neon)]"
    />
  </label>
);

const Delta = ({ testid, label, icon: Icon, value, intensity, arrived }) => {
  const bright = arrived ? 1 : intensity;
  const color = arrived ? "var(--neon)" : intensity > 0.6 ? "var(--amber)" : "var(--cyan)";
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center rounded-sm border border-[var(--border)] p-3"
      style={{ background: `linear-gradient(180deg, rgba(0,0,0,0) 0%, ${color}${Math.round(bright * 25).toString(16).padStart(2, "0")} 100%)` }}
    >
      <Icon size={30} weight="bold" style={{ color }} />
      <div className="mt-1 text-[10px] tracking-widest text-[var(--text-dim)]">{label}</div>
      <div className="readout mt-0.5 text-lg tabular-nums" style={{ color }}>
        {value === null || value === undefined ? "—" : fmt(value, 3) + " m"}
      </div>
    </div>
  );
};
